import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

interface RuleDefinition {
  name: string;
  description?: string;
  importance?: string;
  reasoning?: string;
  rule_responded?: boolean;
  rule_conceded?: boolean;
  [key: string]: unknown;
}

interface DetectionReport {
  rule_match: boolean;
  status?: string;
  sections?: unknown[];
  bookmark_name?: string | string[] | string[][];
  explanation?: string;
  reasoning?: string;
  [key: string]: unknown;
}

interface ChangeDetectionReport {
  matching_changes?: unknown[];
  all_changes?: unknown[];
  explanation?: string;
  [key: string]: unknown;
}

interface ActionReport {
  target_bookmark_names?: string[];
  target_bookmarks?: string[];
  /** Proposal engine only: absent on a report a production review wrote. */
  target_texts?: RedlineTextProposal;
  action_tracked_changes?: Array<{ bookmark_name?: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface ActionStep {
  action_report?: ActionReport;
  [key: string]: unknown;
}

interface TargetReport {
  bookmark_name?: string[] | string[][];
  [key: string]: unknown;
}

export interface ActionPlanEntry {
  steps?: ActionStep[];
  action_plan?: ActionPlanEntry[];
  action?: RuleDefinition;
  target_report?: TargetReport;
  change_detection_report?: ChangeDetectionReport;
  detection_report?: DetectionReport;
  rule?: RuleDefinition;
  match?: boolean;
}

export interface RuleReportEntry {
  rule: RuleDefinition;
  detection_report: DetectionReport;
  change_detection_report?: ChangeDetectionReport;
  action_plan?: ActionPlanEntry[];
  match?: boolean;
  importance?: string;
  status?: string;
  rule_responded?: boolean;
  rule_conceded?: boolean;
  [key: string]: unknown;
}

interface RiskScorableRule {
  match?: boolean;
  rule_responded?: boolean;
  rule_conceded?: boolean;
  rule?: { rule_responded?: boolean; rule_conceded?: boolean };
}

export const DEFAULT_IMPORTANCE = 'High';

/**
 * Calculate the risk score based on the number of triggered rules
 * @returns Risk score percentage (0-100)
 */
export function calculateRiskScore(rules: RiskScorableRule[]): number {
  if (!rules || rules.length === 0) {
    return 0;
  }

  const totalRules = rules.length;
  // Handle both nested (r.rule.rule_responded) and direct (r.rule_responded) property access
  const notTriggered = rules.filter((r) => {
    const isResponded = r.rule?.rule_responded ?? r.rule_responded;
    const isConceded = r.rule?.rule_conceded ?? r.rule_conceded;
    return r.match === false || isResponded === true || isConceded === true;
  }).length;

  // Risk score is the percentage of triggered rules
  return totalRules > 0 ? 100 - Math.round((notTriggered / totalRules) * 100) : 0;
}

export function getAllActionPlans(rule: ActionPlanEntry): ActionPlanEntry[] {
  if (rule.steps) {
    return [rule];
  }
  return rule.action_plan?.flatMap((action) => getAllActionPlans(action)) || [];
}

export function flattenActionPlans(actionPlan: ActionPlanEntry[]): ActionPlanEntry[] {
  return actionPlan.flatMap((action) => action.action_plan ? flattenActionPlans(action.action_plan) : action);
}

export function parseReport(report: RuleReportEntry[] | null | undefined): RuleReportEntry[] {
  const rules: RuleReportEntry[] = [];
  if (!report) {
    return rules;
  }
  for (const rule of report) {
    rule.match = rule.detection_report.rule_match;
    rule.importance = rule.rule.importance || DEFAULT_IMPORTANCE;
    rule.status = rule.detection_report.status;
    if (rule.rule.rule_responded === undefined) {
      rule.rule.rule_responded = false;
    }
    if (rule.rule.rule_conceded === undefined) {
      rule.rule.rule_conceded = false;
    }

    rules.push(rule);
    if (
      rule.change_detection_report &&
      rule.change_detection_report.matching_changes &&
      rule.change_detection_report.matching_changes.length &&
      rule.action_plan &&
      !rule.action_plan.some((ap) => ap.steps)
    ) {
      const rulesPath: ActionPlanEntry[] = [];
      if (getConditionalRuleTree(rule, rulesPath)) {
        const previousRule = rule;
        for (let i = rulesPath.length - 1; i >= 0; i--) {
          const subRule = rulesPath[i];
          subRule.rule = subRule.action as RuleDefinition;
          subRule.rule.name = `${previousRule.rule.name} - ${subRule.rule.name}`;
          subRule.detection_report = {
            ...subRule.detection_report,
            rule_match: true,
            sections: previousRule.detection_report.sections,
          };
          if (subRule.change_detection_report) {
            subRule.change_detection_report.all_changes = previousRule.change_detection_report!.all_changes;
          } else {
            subRule.change_detection_report = { all_changes: previousRule.change_detection_report!.all_changes };
          }
          subRule.rule.description = previousRule.rule.description
            ? `${previousRule.rule.description}\n${subRule.rule.description}`
            : subRule.rule.description;
          subRule.rule.reasoning = rule.rule.reasoning ?? "";
          if (subRule.change_detection_report && subRule.change_detection_report.explanation) {
            subRule.rule.reasoning = subRule.rule.reasoning
              ? `${subRule.rule.reasoning}\n${subRule.change_detection_report.explanation}`
              : subRule.change_detection_report.explanation;
          } else if (subRule.detection_report && subRule.detection_report.explanation) {
            subRule.rule.reasoning = subRule.rule.reasoning
              ? `${subRule.rule.reasoning}\n${subRule.detection_report.explanation}`
              : subRule.detection_report.explanation;
          }
        }
      }
    }
  }
  return rules;
}

/**
 * Creates a sort comparator that orders rules by:
 *  1. Match status — triggered (match=true) before untriggered (match=false)
 *  2. Document position via ruleSectionOrder (ascending)
 *  3. Fallback index (ascending), then optional name for determinism
 */
export function makeRuleDocumentOrderComparator<T extends { match?: boolean }>(
  ruleSectionOrder: Record<string, number> | null,
  getUuid: (item: T) => string,
  getIndex: (item: T) => number,
  getName?: (item: T) => string,
): (a: T, b: T) => number {
  return (a, b) => {
    const matchDiff = (a.match ? 0 : 1) - (b.match ? 0 : 1);
    if (matchDiff !== 0) return matchDiff;

    const posA = ruleSectionOrder?.[getUuid(a)];
    const posB = ruleSectionOrder?.[getUuid(b)];

    const pA = typeof posA === "number" && posA !== Number.MAX_SAFE_INTEGER ? posA : null;
    const pB = typeof posB === "number" && posB !== Number.MAX_SAFE_INTEGER ? posB : null;

    if (pA !== null && pB !== null) return pA - pB;
    if (pA !== null) return -1;
    if (pB !== null) return 1;

    const idxDiff = getIndex(a) - getIndex(b);
    if (idxDiff !== 0) return idxDiff;
    return getName ? getName(a).localeCompare(getName(b)) : 0;
  };
}

/**
 * Collects all _dioptra_p_ bookmark names associated with a rule report entry,
 * covering every field that the backend may populate:
 *   - action_report.target_bookmark_names  (primary, apply.py)
 *   - action_report.target_bookmarks       (legacy alias)
 *   - action_report.action_tracked_changes[*].bookmark_name
 *   - action.target_report.bookmark_name   (conditional rules)
 * Falls back to detection_report.bookmark_name when the action plan yields nothing.
 */
export function extractRuleBookmarkNames(rule: RuleReportEntry): string[] {
  const bms = new Set<string>();

  for (const action of rule.action_plan ?? []) {
    for (const step of action.steps ?? []) {
      const ar = step.action_report;
      if (Array.isArray(ar?.target_bookmark_names)) {
        for (const bm of ar.target_bookmark_names) {
          if (bm) bms.add(bm);
        }
      }
      if (Array.isArray(ar?.target_bookmarks)) {
        for (const bm of ar.target_bookmarks) {
          if (bm) bms.add(bm);
        }
      }
      for (const change of ar?.action_tracked_changes ?? []) {
        if (change.bookmark_name) bms.add(change.bookmark_name);
      }
    }
    // target_report bookmarks (conditional rules — nested array detection matches getRuleBookmarkNames)
    if (action.target_report?.bookmark_name) {
      const bookmarks = Array.isArray(action.target_report.bookmark_name[0])
        ? action.target_report.bookmark_name[0]
        : [action.target_report.bookmark_name[0]];
      for (const bm of bookmarks) {
        if (bm) bms.add(bm);
      }
    }
  }

  if (bms.size === 0) {
    const drBm = rule.detection_report?.bookmark_name;
    if (drBm) {
      const drBms = Array.isArray(drBm) ? (drBm as string[]).flat() : [drBm];
      for (const bm of drBms) {
        if (bm) bms.add(bm as string);
      }
    }
  }

  return Array.from(bms);
}

function getConditionalRuleTree(rule: Pick<RuleReportEntry, "action_plan">, rulesPath: ActionPlanEntry[]): boolean {
  for (const action of rule.action_plan ?? []) {
    if (action.steps) {
      rulesPath.push(action);
      return true;
    } else if (action.action_plan) {
      const found = getConditionalRuleTree(action, rulesPath);
      if (found) {
        rulesPath.push(action);
        return true;
      }
    }
  }
  return false;
}
